import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations } from "@/i18n/translations";
import { api } from "@/lib/api";

const LanguageContext = createContext(null);
const STORAGE_KEY = "gmoaa_lang";

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_KEY) || "en");
  const [site, setSite] = useState(null); // full /api/site-content bundle
  const [labels, setLabels] = useState({}); // { key: {en, gu} } overrides

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  useEffect(() => {
    api.siteContent().then((d) => { setSite(d); setLabels(d.labels || {}); }).catch(() => {});
    // real visitor counter: once per browser session
    try {
      if (!sessionStorage.getItem("gmoaa_visited")) {
        sessionStorage.setItem("gmoaa_visited", "1");
        api.trackVisit().catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, []);

  const toggle = useCallback(() => setLang((l) => (l === "en" ? "gu" : "en")), []);

  // t(key): admin label override first, then built-in translations
  const t = useCallback(
    (key) => {
      const o = labels[key];
      if (o && o[lang]) return o[lang];
      if (o && o.en && lang === "en") return o.en;
      return translations[lang][key] ?? translations.en[key] ?? key;
    },
    [lang, labels]
  );
  const tf = useCallback(
    (obj, base) => {
      if (!obj) return "";
      return obj[`${base}_${lang}`] ?? obj[`${base}_en`] ?? "";
    },
    [lang]
  );
  const pick = useCallback((obj) => (obj ? obj[lang] ?? obj.en : ""), [lang]);

  const value = useMemo(
    () => ({ lang, setLang, toggle, t, tf, pick, site }),
    [lang, toggle, t, tf, pick, site]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};

// Convenience accessors for admin-editable site content (null while loading).
export const useSite = () => {
  const { site, lang } = useLang();
  return { site, lang };
};
